import { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { validateMobile, getExpectedLength, setCountryDigitRules, CountryDigitRule } from '../utils/countryCodes';

export interface CountryCodeItem {
    code: string;
    country?: string;
    flag?: string;
}

const toNumber = (v: any): number | undefined => {
    if (v === undefined || v === null || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
};

export const useCountryCodeSelection = (enabled: boolean = true) => {
    const [countryCodes, setCountryCodes] = useState<CountryCodeItem[]>([]);
    const [selectedCountryCode, setSelectedCode] = useState('+91');
    // Several countries share a dial code (e.g. +1 for US, Canada, Puerto Rico), so the
    // code alone can't identify the picked country — track its flag too.
    const [selectedFlag, setSelectedFlag] = useState<string | undefined>(undefined);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    // The dropdown's search box; cleared each time the dropdown closes.
    const [countrySearch, setCountrySearch] = useState('');
    useEffect(() => { if (!isDropdownOpen) setCountrySearch(''); }, [isDropdownOpen]);

    const setSelectedCountryCode = useCallback((code: string, flag?: string) => {
        setSelectedCode(code);
        setSelectedFlag(flag);
    }, []);

    const currentCountry =
        countryCodes.find(c => c.code === selectedCountryCode && !!selectedFlag && c.flag === selectedFlag) ||
        countryCodes.find(c => c.code === selectedCountryCode);
    const selectedFlagCode = currentCountry?.flag || 'in';

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const fetchCountryCodes = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'country_codes'));
                const fetchedCodes: CountryCodeItem[] = [];
                const digitRules: Record<string, CountryDigitRule> = {};
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    const rawCode = data.dialCode;
                    if (rawCode) {
                        const codeStr = rawCode.startsWith('+') ? rawCode : `+${rawCode}`;
                        fetchedCodes.push({
                            code: codeStr,
                            country: data.name || '',
                            flag: data.flag || '',
                        });
                        // Some countries define an exact `digits` count, others a
                        // `minDigits`/`maxDigits` range (e.g. Germany: 10–11).
                        const digits = toNumber(data.digits);
                        const minDigits = toNumber(data.minDigits);
                        const maxDigits = toNumber(data.maxDigits);
                        if (digits != null || minDigits != null || maxDigits != null) {
                            digitRules[codeStr] = { digits, minDigits, maxDigits };
                        }
                    }
                });
                if (fetchedCodes.length > 0) {
                    fetchedCodes.sort((a, b) => {
                        if (a.code === '+91') return -1;
                        if (b.code === '+91') return 1;
                        return a.code.localeCompare(b.code);
                    });
                    setCountryCodes(fetchedCodes);
                    setCountryDigitRules(digitRules);
                    const has91 = fetchedCodes.some(c => c.code === '+91');
                    setSelectedCode(has91 ? '+91' : fetchedCodes[0].code);
                    setSelectedFlag(undefined);
                }
            } catch (error) {
                console.error('Error fetching country codes from Firestore:', error);
            }
        };

        fetchCountryCodes();
    }, [enabled]);

    /**
     * Helper to process mobile number change when country code changes.
     * Returns the sliced mobile string and any validation error message if invalid.
     */
    const processMobileOnCountryChange = (
        newCode: string,
        currentMobile: string,
        options?: { fullLengthMsg?: boolean }
    ) => {
        const expectedLen = getExpectedLength(newCode);
        const slicedMobile = currentMobile.replace(/\D/g, '').slice(0, expectedLen);
        let errorMessage: string | undefined = undefined;

        if (slicedMobile.length > 0) {
            const isValid = validateMobile(newCode, slicedMobile);
            if (!isValid) {
                if (slicedMobile.length !== expectedLen) {
                    errorMessage = options?.fullLengthMsg
                        ? `Mobile number must be exactly ${expectedLen} digits`
                        : `Exactly ${expectedLen} digits`;
                } else {
                    errorMessage = 'Invalid phone number format';
                }
            }
        }

        return {
            expectedLen,
            slicedMobile,
            errorMessage,
        };
    };

    return {
        countryCodes,
        countrySearch,
        setCountrySearch,
        // countryCodes narrowed by the search box — what the dropdown lists.
        shownCountryCodes: filterCountryCodes(countryCodes, countrySearch),
        selectedCountryCode,
        setSelectedCountryCode,
        isDropdownOpen,
        setIsDropdownOpen,
        dropdownRef,
        selectedFlagCode,
        processMobileOnCountryChange,
    };
};

/** Countries whose name contains the query, or whose dial code starts with it
 *  ("ind", "+44", "971"). An empty query keeps the whole list. */
export const filterCountryCodes = <T extends { code: string; country?: string }>(list: T[], query: string): T[] => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    const digits = q.replace('+', '');
    return list.filter(c => (c.country || '').toLowerCase().includes(q) || (!!digits && c.code.replace('+', '').startsWith(digits)));
};
